import { z } from 'zod'

// Project validation schemas
export const createProjectSchema = z.object({
  title: z
    .string()
    .min(3, 'Title must be at least 3 characters')
    .max(200, 'Title must be less than 200 characters'),
  description: z
    .string()
    .min(10, 'Description must be at least 10 characters')
    .max(5000, 'Description must be less than 5000 characters'),
  skillIds: z
    .array(z.number())
    .min(1, 'Please select at least one skill')
    .max(10, 'Maximum 10 skills allowed'),
  budgetMin: z.number().positive().optional(),
  budgetMax: z.number().positive().optional(),
})

export type CreateProjectFormData = z.infer<typeof createProjectSchema>

// Authentication validation schemas
export const loginSchema = z.object({
  username: z
    .string()
    .min(1, 'Username is required')
    .min(3, 'Username must be at least 3 characters')
    .max(150, 'Username must be less than 150 characters'),
  password: z
    .string()
    .min(1, 'Password is required')
    .min(6, 'Password must be at least 6 characters'),
})

export const signupSchema = z
  .object({
    accountType: z.enum(['client', 'developer']),
    username: z
      .string()
      .min(1, 'Username is required')
      .min(3, 'Username must be at least 3 characters')
      .max(150, 'Username must be less than 150 characters')
      .regex(
        /^[a-zA-Z0-9_-]+$/,
        'Username can only contain letters, numbers, underscores and hyphens'
      ),
    email: z.string().min(1, 'Email is required').email('Please enter a valid email'),
    password: z
      .string()
      .min(1, 'Password is required')
      .min(8, 'Password must be at least 8 characters')
      .regex(
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
        'Password must contain at least one lowercase letter, one uppercase letter, and one number'
      ),
    confirmPassword: z.string().min(1, 'Please confirm your password'),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords don't match",
    path: ['confirmPassword'],
  })

export type LoginFormData = z.infer<typeof loginSchema>
export type SignupFormData = z.infer<typeof signupSchema>

export const passwordResetRequestSchema = z.object({
  email: z.string().min(1, 'Email is required').email('Please enter a valid email'),
})

export const passwordResetConfirmSchema = z
  .object({
    newPassword: z
      .string()
      .min(1, 'Password is required')
      .min(8, 'Password must be at least 8 characters')
      .regex(
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
        'Password must contain at least one lowercase letter, one uppercase letter, and one number'
      ),
    confirmPassword: z.string().min(1, 'Please confirm your password'),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: "Passwords don't match",
    path: ['confirmPassword'],
  })

export type PasswordResetRequestFormData = z.infer<typeof passwordResetRequestSchema>
export type PasswordResetConfirmFormData = z.infer<typeof passwordResetConfirmSchema>

export const profileFormSchema = z.object({
  firstName: z.string().max(150, 'First name must be 150 characters or less').optional(),
  lastName: z.string().max(150, 'Last name must be 150 characters or less').optional(),
  bio: z.string().max(2000, 'Bio must be 2000 characters or less').optional(),
  company: z.string().max(150, 'Company name must be 150 characters or less').optional(),
  location: z.string().max(150, 'Location must be 150 characters or less').optional(),
})

export type ProfileFormData = z.infer<typeof profileFormSchema>
